import { useMemo, useState } from 'react';
import useDownloads from '../hooks/useDownloads';
import { AGENT_GUIDE, startAgentDownload } from '../utils/agentDownloads';
import '../styles/page-download.css';
import Seo from '../components/Seo';
import { SITE_URL } from '../config';

const AppleMark = () => (
  <svg aria-hidden="true" width="30" height="30" viewBox="0 0 24 24"><path d="M16.4 12.7c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.86-1.6 0-3.1 1-4 2.4-1.7 3-0.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.2-.05 1.7-.8 3.2-.8s1.9.8 3.2.77c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.03-.01-2.6-1-2.6-3.9zM14 5.6c.7-.8 1.1-1.9 1-3-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.8-1.5z" fill="#1a1a1a" /></svg>
);
const WindowsMark = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24"><path d="M3 5.5L10.5 4.4V11H3zM11.5 4.2L21 3v8h-9.5zM3 12h7.5v6.6L3 17.5zM11.5 12H21v9l-9.5-1.4z" fill="#366EB6" /></svg>
);
const DlIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="dlico"><path d="M10 3v9m0 0 3.4-3.4M10 12 6.6 8.6M4 15.5h12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const LinuxMark = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24"><circle cx="12" cy="9" r="5.5" fill="none" stroke="#b45318" strokeWidth="2.2" /><path d="M8 13.5L6 21l6-2.5L18 21l-2-7.5" fill="none" stroke="#b45318" strokeWidth="2.2" strokeLinejoin="round" /></svg>
);

export default function DownloadAllPage() {
  const [product, setProduct] = useState('desktop');
  const desktop = product === 'desktop';

  /* Desktop App installers resolve the same way the live page resolves them.
     Background Agent installers go through the stealth-agent download route
     (with the host's S3 presign fallback). */
  const { urls, versions, startDownload } = useDownloads();

  /* Highlight the card for the visitor's own OS, the way the live page does.
     Presentation only — every download stays available to everyone. */
  const detected = useMemo(() => {
    if (typeof navigator === 'undefined') return '';
    const ua = `${navigator.userAgent} ${navigator.platform || ''}`.toLowerCase();
    if (ua.includes('mac')) return 'mac';
    if (ua.includes('win')) return 'windows';
    if (ua.includes('linux') || ua.includes('x11')) return 'linux';
    return '';
  }, []);

  const displayVersion =
    versions.windows || versions['mac-arm64'] || versions['mac-intel'] || versions.ubuntu || '';
  const [busy, setBusy] = useState('');


  const dlAgent = (key) => async () => {
    setBusy(key);
    try {
      await startAgentDownload(key);
    } catch {
      window.alert('Download not available right now. Please try again in a moment.');
    } finally {
      setTimeout(() => setBusy(''), 1500);
    }
  };

  return (
    <>
      {/* Served at both /download and /download/all — one canonical so they don't compete. */}
      <Seo canonical={`${SITE_URL}/download`} title="Download TeamGrid — Desktop App & Background Agent | TeamGrid™" description="Pick your install: the full TeamGrid desktop app with offline time tracking, or the lightweight background agent. Both run on macOS, Windows and Linux." />
<header className="d-hero">
  <div className="container">
    {/* Version badge, matching the live page. Reads the resolved build rather
        than a hard-coded string, so it can't go stale. */}
    <span className="hero-badge dl-ver" style={{margin: '0 auto'}}>
      <i className="dl-dot" aria-hidden="true" />
      {desktop ? 'Desktop App' : 'Background Agent'}
      {desktop && displayVersion ? ` · Version ${displayVersion}` : ''}
    </span>
    <h1 className="sect-h2" style={{fontSize: '52px', lineHeight: '60px', letterSpacing: '-2.1px', marginTop: '20px'}}>Two ways to install.<br /><span className="dim">One workspace.</span></h1>
    <p className="sect-sub">{desktop
      ? 'The full desktop application: automatic time tracking, offline mode, manual start and stop.'
      : 'A lightweight agent that installs in minutes and runs quietly, reporting to your workspace.'}</p>

    <div className="tabs" style={{marginTop: '28px'}} role="tablist" aria-label="Choose a TeamGrid product">
      <button type="button" role="tab" aria-selected={desktop} className={`tab${desktop ? ' on' : ''}`} onClick={() => setProduct('desktop')}>DESKTOP APP</button>
      <button type="button" role="tab" aria-selected={!desktop} className={`tab${!desktop ? ' on' : ''}`} onClick={() => setProduct('agent')}>BACKGROUND AGENT</button>
    </div>

    {desktop ? (
      <div className="oscards">
        <div className={`oscard${detected === 'mac' ? ' picked' : ''}`}>
          {detected === 'mac' && <span className="os-pick">YOUR PLATFORM</span>}
          <span className="osi" style={{background: '#f4f6f7'}}><AppleMark /></span>
          <h3>TeamGrid for macOS</h3>
          <div className="osv">Requires macOS 12.0 or later</div>
          <div className="dlrows">
            <button type="button" className="dlrow" onClick={() => startDownload(urls['mac-arm64'])}>
              <span>Apple Silicon<em>M1/M2/M3</em></span><DlIcon />
            </button>
            <button type="button" className="dlrow" onClick={() => startDownload(urls['mac-intel'])}>
              <span>Intel Processor</span><DlIcon />
            </button>
          </div>
          <details className="dlhelp">
            <summary>Not sure which processor?</summary>
            <p>Apple menu → About This Mac. If it names an M-series chip, take Apple Silicon.</p>
          </details>
        </div>

        <div className={`oscard${detected === 'windows' ? ' picked' : ''}`}>
          {detected === 'windows' && <span className="os-pick">YOUR PLATFORM</span>}
          <span className="osi" style={{background: '#eaf2fb'}}><WindowsMark /></span>
          <h3>TeamGrid for Windows</h3>
          <div className="osv">Windows 10 &amp; 11 (64-bit)</div>
          <div className="dlrows">
            <button type="button" className="dlrow primary" onClick={() => startDownload(urls.windows)}>
              <span>Download for Windows<em>.exe</em></span><DlIcon />
            </button>
          </div>
          <div className="dltags"><span>✓ Auto-updates</span><span>✓ 64-bit</span></div>
          <p className="dlnote">
            <b>Upgrading?</b> Uninstall the current build, delete its Program Files folder and clear app data first.
          </p>
        </div>

        <div className={`oscard${detected === 'linux' ? ' picked' : ''}`}>
          {detected === 'linux' && <span className="os-pick">YOUR PLATFORM</span>}
          <span className="osi" style={{background: '#fdf6e8'}}><LinuxMark /></span>
          <h3>TeamGrid for Linux</h3>
          <div className="osv">Ubuntu 20.04+, Debian</div>
          <div className="dlrows">
            <button type="button" className="dlrow" onClick={() => startDownload(urls.ubuntu)}>
              <span>.deb Package<em>Ubuntu / Debian</em></span><DlIcon />
            </button>
            {/* .AppImage was missing entirely — it's the only option for
                non-Debian distros. */}
            <button type="button" className="dlrow" onClick={() => startDownload(urls['ubuntu-arm64'] || urls.ubuntu)}>
              <span>.AppImage<em>runs anywhere</em></span><DlIcon />
            </button>
          </div>
          <details className="dlhelp">
            <summary>Installing the .AppImage</summary>
            <p>Mark it executable (<code>chmod +x</code>), then launch it. No package manager needed.</p>
          </details>
        </div>
      </div>
    ) : (
      <div className="oscards">
        <div className="oscard">
          <span className="osi" style={{background: '#f4f6f7'}}><AppleMark /></span>
          <h3>Agent for macOS</h3>
          <div className="osv">One-click .dmg · macOS 12+ · Apple Silicon &amp; Intel</div>
          <button className="btn-primary" type="button" onClick={dlAgent('mac-installer')} disabled={busy === 'mac-installer'} style={{width: '100%'}}>{busy === 'mac-installer' ? 'Starting…' : 'Get the Mac agent (Apple Silicon)'}</button>
          <button className="btn-ghost" type="button" onClick={dlAgent('mac-intel-installer')} disabled={busy === 'mac-intel-installer'} style={{width: '100%', marginTop: '8px'}}>{busy === 'mac-intel-installer' ? 'Starting…' : 'Intel Mac'}</button>
          <p style={{fontSize: '11.5px', color: 'var(--ink-faint)', marginTop: '12px'}}>Open the .dmg, click Install. A sign-in window opens on its own. <a href={AGENT_GUIDE.mac} target="_blank" rel="noopener noreferrer" style={{color: 'var(--teal-deep)', fontWeight: 700}}>Install guide (PDF)</a></p>
        </div>
        <div className="oscard">
          <span className="osi" style={{background: '#eaf2fb'}}><WindowsMark /></span>
          <h3>Agent for Windows</h3>
          <div className="osv">One-click .exe · ≈ 2 MB · Windows 10 &amp; 11 · 64-bit</div>
          <button className="btn-primary" type="button" onClick={dlAgent('windows-installer')} disabled={busy === 'windows-installer'} style={{width: '100%'}}>{busy === 'windows-installer' ? 'Starting…' : 'Get the Windows agent'}</button>
          <p style={{fontSize: '11.5px', color: 'var(--ink-faint)', marginTop: '12px'}}>Run the Setup, click Install, sign in once. <a href={AGENT_GUIDE.windows} target="_blank" rel="noopener noreferrer" style={{color: 'var(--teal-deep)', fontWeight: 700}}>Install guide (PDF)</a></p>
        </div>
        <div className="oscard">
          <span className="osi" style={{background: '#fdf6e8'}}><LinuxMark /></span>
          <h3>Agent for Linux</h3>
          <div className="osv">.zip · ≈ 40 MB · extract and run</div>
          <button className="btn-primary" type="button" onClick={dlAgent('linux')} disabled={busy === 'linux'} style={{width: '100%'}}>{busy === 'linux' ? 'Starting…' : 'Get the Linux agent'}</button>
          <p style={{fontSize: '11.5px', color: 'var(--ink-faint)', marginTop: '12px'}}>No package manager needed. Extract, run, sign in once. <a href={AGENT_GUIDE.linux} target="_blank" rel="noopener noreferrer" style={{color: 'var(--teal-deep)', fontWeight: 700}}>Install guide (PDF)</a></p>
        </div>
      </div>
    )}
  </div>
</header>

<section className="sect" style={{paddingTop: '72px'}}>
  <div className="container">
    <h2 className="sect-h2">Which one<br /><span className="dim">do I need?</span></h2>
    <p className="sect-sub">Both report to the same workspace and follow the same rule: no screenshots, no keystrokes, patterns not people.</p>

    <div className="frow">
      <div className="fr-copy">
        <span className="fl" style={{color: '#2DBDB8'}}>DESKTOP APP</span>
        <h3>The full app, with a tray icon and controls</h3>
        <p className="fd">A double-click installer wizard, dashboard access from the tray, offline mode, and manual start and stop. Best for individuals and teams who want to see and steer their own tracking.</p>
        <ul className="fpts">
          <li><span className="sarr">→</span>Tray icon, live status, start and stop by hand</li>
          <li><span className="sarr">→</span>Dashboard reachable without opening a browser</li>
          <li><span className="sarr">→</span>Windows .exe, macOS .dmg, Linux .deb and .AppImage</li>
        </ul>
      </div>
      <div className="frame">
        <div className="frame-h"><span className="dots"><span style={{background: '#ff5f57'}}></span><span style={{background: '#febc2e'}}></span><span style={{background: '#28c840'}}></span></span>TEAMGRID — TRAY</div>
        <div className="applist" style={{padding: '16px 18px'}}>
          <span className="ap">Tracking<span className="bar"><b style={{width: '100%', background: '#34A851'}}></b></span><span className="pc">ON</span></span>
          <span className="ap">Today<span className="bar"><b style={{width: '62%', background: '#2DBDB8'}}></b></span><span className="pc">5.1h</span></span>
          <span className="ap">Offline queue<span className="bar"><b style={{width: '14%', background: '#F2A73A'}}></b></span><span className="pc">0</span></span>
          <span className="ap">Idle since<span className="bar"><b style={{width: '8%', background: '#dfe3e9'}}></b></span><span className="pc">—</span></span>
        </div>
      </div>
    </div>

    <div className="frow rev">
      <div className="frame">
        <div className="frame-h"><span className="dots"><span style={{background: '#ff5f57'}}></span><span style={{background: '#febc2e'}}></span><span style={{background: '#28c840'}}></span></span>BACKGROUND AGENT — STATUS</div>
        <div className="applist" style={{padding: '16px 18px'}}>
          <span className="ap">Install size<span className="bar"><b style={{width: '10%', background: '#469FD8'}}></b></span><span className="pc">3 MB</span></span>
          <span className="ap">Sign-ins needed<span className="bar"><b style={{width: '6%', background: '#6C59A6'}}></b></span><span className="pc">1</span></span>
          <span className="ap">Visible windows<span className="bar"><b style={{width: '4%', background: '#dfe3e9'}}></b></span><span className="pc">0</span></span>
          <span className="ap">Reports to<span className="bar"><b style={{width: '88%', background: '#2DBDB8'}}></b></span><span className="pc">Workspace</span></span>
        </div>
      </div>
      <div className="fr-copy">
        <span className="fl" style={{color: '#469FD8'}}>BACKGROUND AGENT</span>
        <h3>Install it once, then forget it</h3>
        <p className="fd">A small installer that runs quietly and reports to your workspace. No tray icon, no controls to learn. Best when your workspace administrator manages tracking centrally across a fleet.</p>
        <ul className="fpts">
          <li><span className="sarr">→</span>Sign in once, never asked again</li>
          <li><span className="sarr">→</span>Windows &amp; macOS one-click installers (.exe / .dmg)</li>
          <li><span className="sarr">→</span>Linux ships as a .zip: extract and run</li>
        </ul>
      </div>
    </div>

    <p className="roi-note" style={{textAlign: 'center', marginTop: '18px'}}>Install whichever your workspace administrator recommends. Switching later costs nothing.</p>
  </div>
</section>

<section className="sect" style={{paddingTop: '0'}}>
  <div className="container">
    <h2 className="sect-h2" style={{fontSize: '34px', lineHeight: '42px'}}>Built for real work</h2>
    <p className="sect-sub">Native behaviour the browser cannot give you.</p>
    <div className="roi-cards" style={{marginTop: '40px'}}>
      <div className="roi-card"><div className="rl">OFFLINE MODE</div><div className="rv">0 lost</div><p>Tracking continues when the connection drops, then syncs the moment you are back.</p></div>
      <div className="roi-card"><div className="rl">AUTO-PAUSE</div><div className="rv">Idle</div><p>Idle detection pauses the moment you step away and resumes when you return.</p></div>
      <div className="roi-card"><div className="rl">MAC AGENT</div><div className="rv">3 MB</div><p>One-click .dmg. Native speed, no drag on the machine.</p></div>
      <div className="roi-card"><div className="rl">SCREENSHOTS</div><div className="rv">0</div><p>Ever. No keystrokes either. Patterns, not people.</p></div>
    </div>
  </div>
</section>

<section className="sect" style={{paddingTop: '24px'}}>
  <div className="container">
    <h2 className="sect-h2" style={{fontSize: '34px', lineHeight: '42px'}}>Up and running in 3 steps</h2>
    <p className="sect-sub">{desktop ? 'Desktop App' : 'Background Agent'}</p>
    {desktop ? (
      <div className="steps">
        <div className="step"><span className="sn">1</span><h4>Download and open</h4><p>Grab the installer for your OS and double-click to start the setup wizard.</p></div>
        <div className="step"><span className="sn">2</span><h4>Sign in</h4><p>Use your TeamGrid credentials. SSO is supported on Enterprise plans.</p></div>
        <div className="step"><span className="sn">3</span><h4>Start tracking</h4><p>The app drops into your tray. Work as usual, we handle the rest.</p></div>
      </div>
    ) : (
      <div className="steps">
        <div className="step"><span className="sn">1</span><h4>Download</h4><p>Choose the agent for your operating system from the cards above.</p></div>
        <div className="step"><span className="sn">2</span><h4>Run the installer</h4><p>On Windows, run the .exe Setup. On macOS, open the .dmg and click Install. A sign-in window opens automatically.</p></div>
        <div className="step"><span className="sn">3</span><h4>Sign in once</h4><p>One sign-in with your work account. You will not be asked again.</p></div>
      </div>
    )}
    <div className="badges" style={{marginTop: '52px'}}>
      <span className="badge"><span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#2DBDB8', display: 'inline-block'}}></span>SOC 2 TYPE II</span>
      <span className="badge"><span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#34A851', display: 'inline-block'}}></span>ENCRYPTED AT REST &amp; IN TRANSIT</span>
      <span className="badge"><span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#469FD8', display: 'inline-block'}}></span>USER-CONTROLLED VISIBILITY</span>
      <span className="badge"><span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#6C59A6', display: 'inline-block'}}></span>NO SCREENSHOTS</span>
    </div>
  </div>
</section>

<section className="sect" style={{padding: '8px 0 24px'}}>
  <div className="container">
    <h2 className="sect-h2" style={{fontSize: '28px', lineHeight: '36px'}}>Frequently asked</h2>
    <div className="faq">
      <details><summary>What&apos;s the difference between the Desktop App and the Background Agent?</summary><p>The Desktop App is the full application: tray icon, dashboard access, offline mode, manual start and stop. The Background Agent is a lightweight installer that runs silently and reports to your workspace. Install whichever your workspace administrator recommends.</p></details>
      <details><summary>How do I install TeamGrid?</summary><p>Pick your product and operating system above, download the installer, and follow the on-screen steps. The Desktop App is a double-click wizard. The Background Agent on Windows and macOS uses a one-click installer (.exe / .dmg).</p></details>
      <details><summary>What are the system requirements?</summary><p>macOS 12.0 or later, Windows 10 and 11 (64-bit), and most modern Linux distributions including Ubuntu, Fedora and Debian. You need an internet connection for the first sign-in only.</p></details>
    </div>
  </div>
</section>

<div className="container"><div className="workswith"><span className="wl">WORKS BEST WITH</span><a href="/download">Agent download →</a><a href="/monitoring">Workforce Analytics →</a></div></div>

<section className="final" style={{paddingTop: '48px'}}>
  <div className="container-wide">
    <div className="final-band" style={{paddingBottom: '88px'}}>
      <h2>Installed by lunch.<br />Answering questions by Friday.</h2>
      <a className="btn-white" href="/register">Get started FREE</a>
    </div>
  </div>
</section>
    </>
  );
}
